<template>
  <div/>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { useStore } from 'vuex';
import { latestMessage, type WebSocketMessageWithFields, type WebSocketMessageEmpty } from '@/plugins/ws';

import {RootState} from "@/store";

const store = useStore<RootState>();

defineOptions({
  name: 'dataTranslateLanguageEntities',
});

// Базовый тип для сообщений с ID перевода языковой сущности
type TranslateLanguageEntityWithId = {
  idTranslateLanguageEntity: number;
};

// Базовый тип для общих полей перевода языковой сущности
type TranslateLanguageEntityCommonFields = {
  one: string;
  many: string;
};

// Типы для WebSocket сообщений компонента translateLanguageEntities
type TranslateLanguageEntitiesListMessage = WebSocketMessageWithFields<'language-entity-translations' | 'translate-language-entity-list', {
  translateLanguageEntities: unknown[];
}>;
type TranslateLanguageEntitiesNotFoundMessage = WebSocketMessageEmpty<'language-entity-translations-not-found'>;
type TranslateLanguageEntityCreateMessage = WebSocketMessageWithFields<'translate-language-entity-create' | 'translate-language-entity-create-success', TranslateLanguageEntityWithId & {
  idLanguageEntity: number;
  idLanguage: number;
} & TranslateLanguageEntityCommonFields>;
type TranslateLanguageEntityUpdateMessage = WebSocketMessageWithFields<'translate-language-entity-update' | 'translate-language-entity-update-success', TranslateLanguageEntityWithId & TranslateLanguageEntityCommonFields>;
type TranslateLanguageEntityDeleteMessage = WebSocketMessageWithFields<'translate-language-entity-delete' | 'translate-language-entity-delete-success', TranslateLanguageEntityWithId>;
type TranslateLanguageEntityLockMessage = WebSocketMessageWithFields<'translate-language-entity-lock' | 'translate-language-entity-lock-success', TranslateLanguageEntityWithId>;
type TranslateLanguageEntityUnlockMessage = WebSocketMessageWithFields<'translate-language-entity-unlock' | 'translate-language-entity-unlock-success', TranslateLanguageEntityWithId>;
type TranslateLanguageEntityUpdateGlobalMessage = WebSocketMessageWithFields<'translate-language-entity-update-global', {
  i18n: {
    entityName: string;
    one: string;
    many: string;
  };
}>;

// Объединенный тип для всех сообщений
type TranslateLanguageEntitiesWebSocketMessage = 
  | TranslateLanguageEntitiesListMessage 
  | TranslateLanguageEntitiesNotFoundMessage 
  | TranslateLanguageEntityCreateMessage 
  | TranslateLanguageEntityUpdateMessage 
  | TranslateLanguageEntityDeleteMessage 
  | TranslateLanguageEntityLockMessage 
  | TranslateLanguageEntityUnlockMessage 
  | TranslateLanguageEntityUpdateGlobalMessage;

// WebSocket message handler
const handleWebSocketMessage = (data: TranslateLanguageEntitiesWebSocketMessage): void => {
  if (!data?.signal) return;

  switch (data.signal) {
    case 'language-entity-translations':
      store.dispatch('translateLanguageEntities/wsSetTranslateLanguageEntities', {
        translateLanguageEntities: data.translateLanguageEntities,
      });
      break;

    case 'language-entity-translations-not-found':
      store.dispatch('languageEntity/wsSetEmptyLanguageEntity');
      break;

    case 'translate-language-entity-list':
      store.dispatch('translateLanguageEntities/wsSetTranslateLanguageEntities', {
        translateLanguageEntities: data.translateLanguageEntities,
      });
      break;

    case 'translate-language-entity-create':
    case 'translate-language-entity-create-success':
      store.dispatch('translateLanguageEntities/wsTranslateLanguageEntityCreate', {
        idTranslateLanguageEntity: data.idTranslateLanguageEntity,
        idLanguageEntity: data.idLanguageEntity,
        idLanguage: data.idLanguage,
        one: data.one,
        many: data.many,
      });
      break;

    case 'translate-language-entity-update':
    case 'translate-language-entity-update-success':
      store.dispatch('translateLanguageEntities/wsTranslateLanguageEntityUpdate', {
        idTranslateLanguageEntity: data.idTranslateLanguageEntity,
        one: data.one,
        many: data.many,
      });
      break;

    case 'translate-language-entity-delete':
    case 'translate-language-entity-delete-success':
      store.dispatch('translateLanguageEntities/wsTranslateLanguageEntityDelete', {
        idTranslateLanguageEntity: data.idTranslateLanguageEntity,
      });
      break;

    case 'translate-language-entity-lock':
    case 'translate-language-entity-lock-success':
      store.dispatch('translateLanguageEntities/wsTranslateLanguageEntityLock', {
        idTranslateLanguageEntity: data.idTranslateLanguageEntity,
      });
      break;

    case 'translate-language-entity-unlock':
    case 'translate-language-entity-unlock-success':
      store.dispatch('translateLanguageEntities/wsTranslateLanguageEntityUnlock', {
        idTranslateLanguageEntity: data.idTranslateLanguageEntity,
      });
      break;

    case 'translate-language-entity-update-global':
      store.dispatch('i18n/updateEntityGlobal', {
        entityName: data.i18n.entityName,
        one: data.i18n.one,
        many: data.i18n.many,
      });
      break;
  }
};

watch(latestMessage, handleWebSocketMessage);
</script>
